import type * as LDClient from 'launchdarkly-js-client-sdk'
import { pageActivityStream } from '@/infra/page-activity'
import {
  distinctUntilChanged,
  filter,
  map,
  type Observable,
  skip,
  startWith,
  switchMap,
} from 'rxjs'
import { share } from 'rxjs/operators'
import { castFlagValue } from '@/features/feature-toggles/toggle-util'
import type {
  FeatureToggleClient,
  FeatureToggleUser,
  FlagValue,
} from '@/features/feature-toggles/toggle-client'

/**
 * Basic local-only client that consumes the bootstrapped flags state.
 */
export class LocalLDClient implements FeatureToggleClient {
  // On local, it usually requires losing tab focus to update toggles.
  // Therefore, we can perform a toggle fetch whenever the tab becomes active.
  private readonly _flagsReloaded$ = pageActivityStream().pipe(
    filter((descriptor) => descriptor.visible && descriptor.focused),
    switchMap(() => this._reloadFlags()),
    share(),
  )

  private constructor(private _flagValues: LDClient.LDFlagSet) {}

  /** @inheritdoc */
  variation<T extends FlagValue>(key: string, defaultValue: T): T {
    const flagExists = Object.prototype.hasOwnProperty.call(
      this._flagValues,
      key,
    )

    if (!flagExists) {
      return castFlagValue<T>(null, defaultValue)
    }

    return castFlagValue(this._flagValues[key], defaultValue)
  }

  /** @inheritdoc */
  flagChanges(key: string): Observable<unknown> {
    return this._flagsReloaded$.pipe(
      map(() => this._flagValues[key] as unknown),
      // Start by evaluating immediately, so we prime the `distinct` cache
      startWith(this._flagValues[key] as unknown),
      distinctUntilChanged(),
      // Skip the first item.
      skip(1),
    )
  }

  /** @inheritdoc */
  identify(_user: FeatureToggleUser): Promise<void> {
    // Nothing to do
    return Promise.resolve()
  }

  /**
   * Reloads flags from the server.
   * @private
   */
  private async _reloadFlags() {
    try {
      this._flagValues = (await fetch('/__feature-toggles').then((r) =>
        r.json(),
      )) as LDClient.LDFlagSet
    } catch (err: unknown) {
      console.error('Unable to refresh toggles from server.', err)
    }
  }

  /**
   * Creates a local-only LD client that checks bootstrapped data.
   */
  static create() {
    return new LocalLDClient(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-explicit-any
      (window as any).__ldFlagsState,
    )
  }
}
